// Global
import React, { useState } from 'react';
import sanitizeHtml from 'sanitize-html';

// Local
import styles from './NutritionFacts.module.scss';
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import useDictionary from 'lib/hooks/useDictionary';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import NutritionTableStandard from './NutritionTableStandard';
import NutritionExchanges from './NutritionExchanges';
import { normalizeRecipeNutritionData, INormalizedRecipeNutritionData } from './utils/recipe.utils';
import {
  normalizeProductNutritionData,
  INormalizedProductNutritionData,
} from './utils/product.utils';
import { RecipeResponse } from 'lib/services/models/content-response';
import { ProductNutritionPanels } from 'lib/services/models/product-response';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import EnhancedRichText from '../Atoms/EnhancedRichText/EnhancedRichText';

export interface NutritionFactsProductProps {
  contentType: 'product';
  contentTypeData: ProductNutritionPanels | null;
}
export interface NutritionFactsRecipeProps {
  contentType: 'recipe';
  contentTypeData: RecipeResponse;
}
export type NutritionFactsProps = NutritionFactsProductProps | NutritionFactsRecipeProps;

interface SummaryItem {
  displayName: string;
  quantity: string;
}

export default function NutritionFacts({ contentTypeData, contentType }: NutritionFactsProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let nutritionData: INormalizedProductNutritionData | INormalizedRecipeNutritionData | null = null;
  const { siteName } = useGlobalData();
  const isGMFSProduct = siteName === 'GMFS' && contentType === 'product';
  const hasRecipeNutritionItems =
    contentType === 'recipe' &&
    contentTypeData !== null &&
    contentTypeData.NutritionItems &&
    contentTypeData.NutritionItems.length > 0;
  const hasProductNutritionItems = contentType === 'product' && contentTypeData;
  const [productNutritionIndex, setProductNutritionIndex] = useState(0);
  // Localization dictionary items
  const { getDictionaryValue } = useDictionary();
  const nutritionHeadline = getDictionaryValue(
      'Common.NutritionFacts.Shared.NutritionFactsHeading'
    ),
    nutritionNotAvailableText = getDictionaryValue(
      'Common.NutritionFacts.Shared.NutritionNotAvailableText'
    ),
    seeMoreToggleText = getDictionaryValue('Common.NutritionFacts.Recipe.AccordionButton.SeeMore'),
    seeLessToggleText = getDictionaryValue('Common.NutritionFacts.Recipe.AccordionButton.SeeLess'),
    defaultDisclaimerText = getDictionaryValue(
      'Common.NutritionFacts.Shared.Disclaimers.NutritionDisclaimer'
    );
  const nutritionUnavailableMessage = isGMFSProduct
    ? getDictionaryValue(
        'Components.Product.ProductNutritionUnavailableMessage.ProductNutritionUnavailableMessage',
        nutritionNotAvailableText
      )
    : '';

  // Accordion state for nutrition facts
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const toggleText = accordionExpanded ? seeLessToggleText : seeMoreToggleText;
  const handleAccordionToggle = () => {
    const eventName = accordionExpanded ? 'nutrition_collapse' : 'nutrition_expand';

    const payload: DataLayerEvent = {
      event: eventName,
    };

    pushToDataLayer(payload);
    setAccordionExpanded((prev) => !prev);
  };

  const productNutritionData = hasProductNutritionItems
    ? (normalizeProductNutritionData(contentTypeData, getDictionaryValue, isGMFSProduct) ?? [])
    : [];
  const hasProductNutritionData = productNutritionData.length > 0;
  // Determine which content type data to normalize
  switch (contentType) {
    case 'recipe':
      nutritionData = hasRecipeNutritionItems
        ? normalizeRecipeNutritionData(contentTypeData as RecipeResponse)
        : null;
      break;
    case 'product':
      nutritionData = productNutritionData[productNutritionIndex];
      break;
    default:
      nutritionData = null;
  }
  return (
    <div className={styles.nutritionFacts}>
      {isGMFSProduct && (
        <h2 id="nutritionHeading" className={styles.nutritionHeading}>
          {nutritionHeadline}
        </h2>
      )}
      {isGMFSProduct && productNutritionData.length > 1 && (
        <div className={styles.servingSizeDropdown}>
          <label className={styles.servingSizeLabel} htmlFor="serving-size-select">
            Serving Size
          </label>
          <select
            id="serving-size-select"
            className={styles.servingSizeSelect}
            onChange={(e) => setProductNutritionIndex(parseInt(e.target.value))}
            value={productNutritionIndex}
          >
            {productNutritionData.map((panel, index) => (
              <option key={index} value={index}>
                {panel?.servingSize ?? `Panel ${index + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}
      {isGMFSProduct && productNutritionData.length === 0 && nutritionUnavailableMessage && (
        <EnhancedRichText
          className={styles.nutritionNotAvailable}
          value={sanitizeHtml(nutritionUnavailableMessage, {
            allowedTags: ['strong', 'br', 'a', 'em', 'p'],
            allowedAttributes: { a: ['href'] },
          })}
        />
      )}
      {!isGMFSProduct && (
        <div className={styles.nutritionHeader}>
          <h2 id="nutritionHeading" className={styles.nutritionHeading}>
            {nutritionHeadline}
          </h2>
          {hasRecipeNutritionItems && (
            <CtaButton
              className="printHide"
              type="button"
              ctaStyle="outline"
              ctaVariant="brand"
              onClick={handleAccordionToggle}
              aria-expanded={accordionExpanded}
              aria-controls="nutritionAccordion"
            >
              {toggleText}
            </CtaButton>
          )}
          {!hasRecipeNutritionItems && !hasProductNutritionItems && (
            <div className={styles.nutritionNotAvailable}>{nutritionNotAvailableText}</div>
          )}
        </div>
      )}
      {hasRecipeNutritionItems &&
      (nutritionData as INormalizedRecipeNutritionData)?.recipeNutrition ? (
        <div className={styles.nutritionContentContainer}>
          <div className={styles.summary}>
            <ul className={styles.summaryCardList}>
              {(nutritionData as INormalizedRecipeNutritionData).recipeNutrition.summaryItems &&
                (nutritionData as INormalizedRecipeNutritionData).recipeNutrition.summaryItems.map(
                  (item: SummaryItem, index: number) => (
                    <li key={index} className={styles.summaryCardItem}>
                      <div className={styles.summaryLabel}>{item.displayName}</div>
                      <EnhancedRichText className={styles.summaryValue} value={item.quantity} />
                    </li>
                  )
                )}
            </ul>
          </div>
          <div
            id="nutritionAccordion"
            role="region"
            className={
              accordionExpanded
                ? `${styles.nutritionAccordion} ${styles.expanded}`
                : styles.nutritionAccordion
            }
            aria-labelledby="nutritionHeading"
          >
            <NutritionTableStandard tableData={nutritionData as INormalizedRecipeNutritionData} />
            {hasRecipeNutritionItems && nutritionData && (
              <NutritionExchanges
                exchangeData={
                  (nutritionData as INormalizedRecipeNutritionData).recipeNutrition.exchangeItems ??
                  []
                }
                carbohydrateChoice={
                  (nutritionData as INormalizedRecipeNutritionData).recipeNutrition
                    .carbohydrateChoice || ''
                }
                disclaimer={defaultDisclaimerText}
              />
            )}
          </div>
        </div>
      ) : hasProductNutritionData ? (
        <NutritionTableStandard tableData={nutritionData as INormalizedProductNutritionData} />
      ) : null}
    </div>
  );
}
